import config from '../../config';
import { Api, ApiConfig } from '../api';

export type BrandFeatureState =
    | 'DISABLED_BY_PLAN'
    | 'AVAILABLE_NOT_ENABLED'
    | 'ENABLING'
    | 'ENABLED'
    | 'SUSPENDED_BY_PLAN';

export interface BrandFeatureAvailability {
    state: BrandFeatureState;
    reasonCode?: string | null;
    ctaAction?: string | null;
    planEntitled: boolean;
    brandUiEnabled: boolean;
    maxBrands?: number | null;
    initializationRequestId?: string | null;
    initializationError?: string | null;
}

export interface Brand {
    id: string;
    workspaceId: string;
    name: string;
    slug: string;
    domain?: string | null;
    logoUrl?: string | null;
    description?: string | null;
    status: 'ACTIVE' | 'ARCHIVED';
    /** Spring serializes Java's isDefault accessor as `default`. */
    default?: boolean;
    isDefault?: boolean;
    createdBy?: string | null;
    ownerId?: string | null;
    memberRole?: 'VIEW' | 'EDIT' | null;
    visibility?: 'WORKSPACE' | 'PRIVATE';
}

export interface ProjectUsageByCreator {
    creatorUserId: string;
    workspaceCount: number;
    privateCount: number;
}

export interface ProjectOverviewMembership {
    brandId: string;
    userId: string;
    role: 'VIEW' | 'EDIT';
    status: 'ACTIVE' | 'INACTIVE';
    email?: string | null;
    name?: string | null;
}

export interface ProjectOverviewResponse {
    totalBrandCount: number;
    projectUsageByCreator: ProjectUsageByCreator[];
    brands: Brand[];
    assignmentBrands: Brand[];
    memberships: ProjectOverviewMembership[];
    integrationAssignments: BrandIntegrationAssignment[];
    resourceAssignments: BrandResourceAssignment[];
}

export interface ProjectUserResourceGrant {
    id?: string;
    workspaceId?: string;
    projectId?: string;
    userId: string;
    connectionId: number;
    resourceType: string;
    resourceKey: string;
    accessLevel: 'DISABLED' | 'READ' | 'WRITE';
    grantSource?: 'AUTO_MATERIALIZED' | 'EXPLICIT';
    grantedBy?: string | null;
}

export type Project = Brand;
export type ProjectMember = BrandMember;
export type ProjectResourceAssignment = BrandResourceAssignment;
export type ProjectIntegrationAssignment = BrandIntegrationAssignment;

export interface BrandMember {
    userId: string;
    role: 'VIEW' | 'EDIT';
    status: 'ACTIVE' | 'INACTIVE';
    email?: string | null;
    name?: string | null;
}

export interface BrandResourceAssignment {
    id: string;
    brandId: string;
    provider: string;
    resourceType: string;
    resourceKey: string;
    connectionId?: number | null;
    sourceMappingType: string;
    sourceMappingId: number;
    status: 'ACTIVE' | 'INACTIVE';
    /** A label is scoped to this resource's assignment in one Business group. */
    label?: string | null;
    /** Maximum member access for this assignment in one Business group. */
    maxAccessLevel?: 'DISABLED' | 'READ' | 'WRITE' | null;
}

export interface BrandIntegrationAssignment {
    id: string;
    workspaceId: string;
    projectId: string;
    connectionId: number;
    maxAccessLevel: 'DISABLED' | 'READ' | 'WRITE';
    status: 'ACTIVE' | 'INACTIVE';
    assignedBy?: string | null;
    assignedAt?: string | null;
}

type ProjectAssignmentAccessLevel = 'DISABLED' | 'READ' | 'WRITE';
type ProjectResourceAssignmentInput = Pick<
    BrandResourceAssignment,
    'provider' | 'resourceType' | 'resourceKey' | 'label' | 'maxAccessLevel'
> & { connectionId?: number | null };

export interface ProjectIntegrationMutation {
    projectId: string;
    connectionId: number;
    maxAccessLevel?: ProjectAssignmentAccessLevel;
}

export interface ProjectResourceSyncMutation {
    projectId: string;
    resources: ProjectResourceAssignmentInput[];
    removals: Array<Pick<BrandResourceAssignment, 'provider' | 'resourceType' | 'resourceKey'> & {
        connectionId?: number | null;
    }>;
}

export interface ProjectAssignmentBatch {
    integrationAssignments?: ProjectIntegrationMutation[];
    integrationRemovals?: ProjectIntegrationMutation[];
    resourceSyncs?: ProjectResourceSyncMutation[];
}

/** The server-owned provenance for a display identity. */
export type IntegrationDisplaySource =
    | 'NATIVE_RESOURCE'
    | 'NATIVE_LOCATOR'
    | 'CUSTOM_LABEL'
    | 'RESOURCE_ID'
    | 'UNNAMED';

/** A non-sensitive, server-normalized part of an integration display identity. */
export interface IntegrationDisplayContext {
    key: string;
    label: string;
    value: string;
}

/**
 * The only business-facing identity supplied for resource-selection UI.
 * Connection and resource identifiers remain selection-only fields.
 */
export interface IntegrationDisplayIdentity {
    name: string;
    context?: IntegrationDisplayContext[] | null;
    locator?: string | null;
    source: IntegrationDisplaySource;
}

export interface IntegrationConnection {
    workspaceId: string;
    connectionId: number;
    connectedBy?: string | null;
    connectedByName?: string | null;
    connectedByEmail?: string | null;
    provider: string;
    slugId?: string | null;
    label?: string | null;
    externalAccountId?: string | null;
    externalAccountName?: string | null;
    externalAccountType?: string | null;
    status: string;
    connectedAt?: string | null;
    canUseIntegration?: boolean;
    canManageConnection?: boolean;
    canManageAccess?: boolean;
    display?: IntegrationDisplayIdentity | null;
}

export interface IntegrationContractResult {
    integrations: IntegrationConnection[];
    resources: IntegrationResource[];
    unsupportedProviders: string[];
    complete: boolean;
}

export interface ProjectIntegrationsBootstrapResponse {
    project: Brand;
    integrationAssignments: BrandIntegrationAssignment[];
    resourceAssignments: BrandResourceAssignment[];
    resourceGrants: ProjectUserResourceGrant[];
    resourceContract: IntegrationContractResult;
}

const normalizeIntegrationContractResult = (
    result: IntegrationContractResult | IntegrationResource[],
): IntegrationContractResult => Array.isArray(result)
    ? { integrations: [], resources: result, unsupportedProviders: [], complete: true }
    : {
        integrations: result.integrations || [],
        resources: result.resources || [],
        unsupportedProviders: result.unsupportedProviders || [],
        complete: result.complete !== false,
    };

export interface IntegrationResource {
    workspaceId: string;
    connectionId?: number | null;
    connectedBy?: string | null;
    provider: string;
    resourceType: string;
    resourceKey: string;
    sourceMappingType?: string | null;
    sourceMappingId?: number | null;
    name?: string | null;
    /** Project-scoped display label for this assigned Resource. */
    label?: string | null;
    /** Workspace-scoped label for this independently managed account/resource. */
    workspaceLabel?: string | null;
    status: string;
    /** True when this row came from the current Workspace resource contract. */
    isInResourceContract?: boolean;
    canManageConnection?: boolean;
    canManageResourceAccess?: boolean;
    canUseResource?: boolean;
    /** Legacy workspace-level label, supplied only while group labels migrate. */
    legacyConnectionLabel?: string | null;
    display?: IntegrationDisplayIdentity | null;
}

export class BrandGroupService {
    private readonly api: Api;

    constructor(apiConfig: ApiConfig = { baseURL: config.services.feature.apiHost }) {
        this.api = new Api(apiConfig);
    }

    getFeature(workspaceId: string): Promise<BrandFeatureAvailability> {
        return this.api.get(`/api/workspaces/${workspaceId}/project-feature`);
    }

    enable(workspaceId: string, requestId: string): Promise<BrandFeatureAvailability> {
        return this.api.post(`/api/workspaces/${workspaceId}/project-feature/enable`, {}, {
            headers: { 'Idempotency-Key': requestId },
        });
    }

    listBrands(workspaceId: string): Promise<Brand[]> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects`);
    }

    /** Product-facing alias retained for callers importing projectService from this legacy module. */
    listProjects(workspaceId: string): Promise<Brand[]> {
        return this.listBrands(workspaceId);
    }

    /** Active Projects available as integration-assignment targets. */
    listIntegrationAssignmentBrands(workspaceId: string): Promise<Brand[]> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects?scope=integration-assignment`);
    }

    /** Product-facing alias retained for callers importing projectService from this legacy module. */
    listIntegrationAssignmentProjects(workspaceId: string): Promise<Brand[]> {
        return this.listIntegrationAssignmentBrands(workspaceId);
    }

    listTaskSelectableBrands(workspaceId: string): Promise<Brand[]> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects?scope=task-selection`);
    }

    /** Product-facing alias retained for callers importing projectService from this legacy module. */
    listTaskSelectableProjects(workspaceId: string): Promise<Brand[]> {
        return this.listTaskSelectableBrands(workspaceId);
    }

    countProjects(workspaceId: string): Promise<number> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects/count`);
    }

    getProjectUsageByCreator(workspaceId: string): Promise<ProjectUsageByCreator[]> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects/usage-by-creator`);
    }

    getProjectOverview(workspaceId: string): Promise<ProjectOverviewResponse> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects/overview`);
    }

    createBrand(workspaceId: string, payload: { name: string; slug?: string; domain?: string; description?: string; logoUrl?: string | null; visibility?: 'WORKSPACE' | 'PRIVATE' }): Promise<Brand> {
        return this.api.post(`/api/workspaces/${workspaceId}/projects`, payload);
    }

    updateBrand(workspaceId: string, brandId: string, payload: Partial<Pick<Brand, 'name' | 'slug' | 'description' | 'domain' | 'logoUrl'>>): Promise<Brand> {
        return this.api.patch(`/api/workspaces/${workspaceId}/projects/${brandId}`, payload);
    }

    archiveBrand(workspaceId: string, brandId: string): Promise<void> {
        return this.api.delete(`/api/workspaces/${workspaceId}/projects/${brandId}`);
    }

    listMembers(workspaceId: string, brandId: string): Promise<BrandMember[]> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects/${brandId}/members`);
    }

    upsertMember(workspaceId: string, brandId: string, payload: { userId: string; role: 'VIEW' | 'EDIT' }): Promise<void> {
        return this.api.put(`/api/workspaces/${workspaceId}/projects/${brandId}/members`, payload);
    }

    removeMember(workspaceId: string, brandId: string, userId: string): Promise<void> {
        return this.api.delete(`/api/workspaces/${workspaceId}/projects/${brandId}/members/${userId}`);
    }

    listProjectResourceGrants(workspaceId: string, brandId: string): Promise<ProjectUserResourceGrant[]> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects/${brandId}/resource-grants`);
    }

    updateProjectResourceGrant(
        workspaceId: string,
        brandId: string,
        payload: Omit<ProjectUserResourceGrant, 'id' | 'workspaceId' | 'projectId' | 'grantedBy'>,
    ): Promise<ProjectUserResourceGrant> {
        return this.api.put(`/api/workspaces/${workspaceId}/projects/${brandId}/resource-grants`, payload);
    }

    updateProjectResourceGrants(
        workspaceId: string,
        brandId: string,
        grants: Array<Omit<ProjectUserResourceGrant, 'id' | 'workspaceId' | 'projectId' | 'grantedBy'>>,
    ): Promise<ProjectUserResourceGrant[]> {
        return this.api.put(`/api/workspaces/${workspaceId}/projects/${brandId}/resource-grants/batch`, { grants });
    }

    updateProjectMemberAccessGrants(
        workspaceId: string,
        brandId: string,
        grants: Array<Omit<ProjectUserResourceGrant, 'id' | 'workspaceId' | 'projectId' | 'grantedBy'>>,
    ): Promise<ProjectUserResourceGrant[]> {
        return this.api.put(
            `/api/workspaces/${workspaceId}/projects/${brandId}/resource-grants/member-access-batch`,
            { grants },
        );
    }

    updateProjectMemberResourceGrants(
        workspaceId: string,
        brandId: string,
        member: { userId: string; role: 'VIEW' | 'EDIT' },
        grants: Array<Omit<ProjectUserResourceGrant, 'id' | 'workspaceId' | 'projectId' | 'grantedBy'>>,
    ): Promise<ProjectUserResourceGrant[]> {
        return this.api.put(
            `/api/workspaces/${workspaceId}/projects/${brandId}/resource-grants/member-batch`,
            { member, grants },
        );
    }

    listResources(workspaceId: string, brandId: string): Promise<BrandResourceAssignment[]> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects/${brandId}/resources`);
    }

    listProjectIntegrations(workspaceId: string, brandId: string): Promise<BrandIntegrationAssignment[]> {
        return this.api.get(`/api/workspaces/${workspaceId}/projects/${brandId}/integrations`);
    }

    assignIntegration(workspaceId: string, brandId: string, connectionId: number, maxAccessLevel: 'DISABLED' | 'READ' | 'WRITE' = 'READ'): Promise<BrandIntegrationAssignment> {
        return this.api.put(`/api/workspaces/${workspaceId}/projects/${brandId}/integrations`, { connectionId, maxAccessLevel });
    }

    async assignIntegrations(workspaceId: string, brandId: string, connectionIds: number[], maxAccessLevel: 'DISABLED' | 'READ' | 'WRITE' = 'READ'): Promise<BrandIntegrationAssignment[]> {
        try {
            return await this.api.put(`/api/workspaces/${workspaceId}/projects/${brandId}/integrations/batch`, { connectionIds, maxAccessLevel });
        } catch (error) {
            // Local Admin can be served against an Account API that has not
            // received the companion backend release yet. Keep the operation
            // functional in that short compatibility window; the caller still
            // coalesces its UI refresh into one final reload.
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status !== 404) throw error;
            const assignments: BrandIntegrationAssignment[] = [];
            for (const connectionId of connectionIds) {
                assignments.push(await this.assignIntegration(workspaceId, brandId, connectionId, maxAccessLevel));
            }
            return assignments;
        }
    }

    applyProjectAssignmentBatch(
        workspaceId: string,
        payload: ProjectAssignmentBatch,
    ): Promise<void> {
        // This endpoint is intentionally fail-closed: its contract is one
        // transaction across Projects. Falling back to old per-Project routes
        // would turn a failed save into a partially committed permission change.
        return this.api.put<void>(`/api/workspaces/${workspaceId}/project-assignments/batch`, payload);
    }

    removeIntegration(workspaceId: string, brandId: string, connectionId: number): Promise<void> {
        return this.api.delete(`/api/workspaces/${workspaceId}/projects/${brandId}/integrations`, {
            data: { connectionId },
        });
    }

    getIntegrationContract(workspaceId: string): Promise<IntegrationContractResult> {
        return this.api.get<IntegrationContractResult | IntegrationResource[]>(`/api/workspaces/${workspaceId}/resource-contract`)
            .then(normalizeIntegrationContractResult);
    }

    getProjectIntegrationContract(workspaceId: string, projectId: string): Promise<IntegrationContractResult> {
        return this.api.get<IntegrationContractResult | IntegrationResource[]>(
            `/api/workspaces/${workspaceId}/projects/${projectId}/resource-contract`,
        ).then(normalizeIntegrationContractResult);
    }

    getProjectIntegrationsBootstrap(
        workspaceId: string,
        projectId: string,
    ): Promise<ProjectIntegrationsBootstrapResponse> {
        return this.api.get<ProjectIntegrationsBootstrapResponse>(
            `/api/workspaces/${workspaceId}/projects/${projectId}/integrations/bootstrap`,
        );
    }

    listAvailableResources(workspaceId: string): Promise<IntegrationResource[]> {
        return this.getIntegrationContract(workspaceId).then((result) => result.resources);
    }

    assignResource(workspaceId: string, brandId: string, payload: Pick<BrandResourceAssignment, 'provider' | 'resourceType' | 'resourceKey' | 'label' | 'maxAccessLevel'> & { connectionId?: number | null }): Promise<BrandResourceAssignment> {
        return this.api.put(`/api/workspaces/${workspaceId}/projects/${brandId}/resources`, payload);
    }

    assignResources(
        workspaceId: string,
        brandId: string,
        payloads: ProjectResourceAssignmentInput[],
    ): Promise<BrandResourceAssignment[]> {
        // Resource selection is an atomic contract.  Never fan out to the
        // legacy single-resource endpoint: a partial write would leave the
        // parent/child assignment matrix inconsistent and make a retry look
        // like an authorization change.  Older servers must be upgraded
        // before this client is deployed.
        return this.api.put(`/api/workspaces/${workspaceId}/projects/${brandId}/resources/batch`, {
            resources: payloads,
        });
    }

    syncResources(
        workspaceId: string,
        brandId: string,
        resources: ProjectResourceAssignmentInput[],
        removals: Array<Pick<BrandResourceAssignment, 'provider' | 'resourceType' | 'resourceKey'> & { connectionId?: number | null }>,
    ): Promise<BrandResourceAssignment[]> {
        return this.api.put(
            `/api/workspaces/${workspaceId}/projects/${brandId}/resources/sync`,
            { resources, removals },
        );
    }

    removeResource(workspaceId: string, brandId: string, payload: Pick<BrandResourceAssignment, 'provider' | 'resourceType' | 'resourceKey'> & { connectionId?: number | null }): Promise<void> {
        return this.api.delete(`/api/workspaces/${workspaceId}/projects/${brandId}/resources`, { data: payload });
    }
}

export const brandGroupService = new BrandGroupService();
/** Project is the product-facing name; the legacy export is retained for older callers. */
export const projectService = brandGroupService;
